/* global google */

import React from 'react';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import { fitBounds } from 'google-map-react/utils';
import classNames from 'classnames';

import StoreSearch from './store-search';
import StoreList from './store-list';
import StorePin from './store-pin';
import screenAndStyle from '../_utils/screen-and-style';

const defaultCoordinates = {
  lat: 37.2766945,
  lng: -104.6420907,
  zoom: 4
};

const YourLocationPin = () => (
  <div className='pin your-location-pin' />
);

class StoreFinder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      locations: [],
      mapLoaded: false,
      ...defaultCoordinates
    };
    this.searchContainer = null;
    this.mapContainer = null;
    this.setSearchContainerRef = element => {
      this.searchContainer = element
    };
    this.setMapContainerRef = element => {
      this.mapContainer = element
    };
  }

  onGoogleApiLoaded = ({ map }) => {
    this.map = map;
    this.setState({
      mapLoaded: true
    }, () => {
      const params = new URLSearchParams(window.location.search);
      const address = params.get('address');

      if (!address) {
        this.refs['store-search'].searchCurrentPosition();
      } else {
        this.refs['store-search'].changeAddress(address);
        this.refs['store-list'].setFirstLocationAsDefault();
      }
    });
  }

  changeAddress = ({ locations, coordinates }) => {
    this.setState({
      currentStoreId: undefined,
      locations,
      yourLocation: coordinates,
      ...this.calculateCenterCoordinates(locations, coordinates)
    });
  }

  changeSelectedStore = (location) => {
    const { currentStoreId } = this.state;

    if (!location) {
      this.setState({ currentStoreId: undefined });
      return;
    }

    if (location.id === currentStoreId) return;

    const { latitude: lat, longitude: lng } = location.coordinates;

    this.setState({
      currentStoreId: location.id,
      ...this.applyCoordinateOffset({ lat, lng })
    });
  }

  applyCoordinateOffset({ lat, lng, zoom }) {
    if (screenAndStyle.isMobileBrowser()) {
      return { lat, lng, zoom };
    }
    const zoomOrMapZoom = zoom || this.map.getZoom();

    const offsetX = this.searchContainer.offsetWidth / 2;
    const scale = 2 ** (zoomOrMapZoom);
    const pixelOffset = new google.maps.Point((offsetX / scale), 0);
    const latLng = new google.maps.LatLng(lat, lng);
    const centerPoint = this.map.getProjection().fromLatLngToPoint(latLng);

    const offsetCenterPoint = new google.maps.Point(
      centerPoint.x - pixelOffset.x,
      centerPoint.y + pixelOffset.y
    );

    const offsetLatLng =
      this.map.getProjection().fromPointToLatLng(offsetCenterPoint);

    return {
      lat: offsetLatLng.lat(),
      lng: offsetLatLng.lng(),
      zoom: zoomOrMapZoom,
    }
  }

  calculateCenterCoordinates(locations, originCoords) {
    if (!locations.length) {
      return { ...originCoords, zoom: 13 };
    }

    const closeLocations =
      locations.filter(location => location.distance.miles > 0 && location.distance.miles < 30);

    const latitudes = [];
    const longitudes = [];

    (closeLocations.length ? closeLocations : locations).forEach(location => {
      latitudes.push(location.coordinates.latitude);
      longitudes.push(location.coordinates.longitude);
    });

    const bounds = {
      nw: {
        lat: Math.max(...latitudes),
        lng: Math.min(...longitudes)
      },
      se: {
        lat: Math.min(...latitudes),
        lng: Math.max(...longitudes)
      }
    };

    const mapSize = {
      height: this.mapContainer.offsetHeight,
      width: this.mapContainer.offsetWidth / 2
    };

    const {
      center: { lat, lng }, zoom
    } = fitBounds(bounds, mapSize);

    return this.applyCoordinateOffset({ lat, lng, zoom });
  }

  render() {
    const { urls, apiKey } = this.props;
    const { yourLocation, locations, currentStoreId, mapLoaded, lat, lng, zoom } = this.state;

    const $search = (() => {
      if (!mapLoaded) {
        return null;
      }

      return (
        <div className='search-container' ref={this.setSearchContainerRef}>
          <StoreSearch
            ref='store-search'
            onAddressChanged={this.changeAddress}
            urls={urls} />

          <StoreList
            ref='store-list'
            locations={locations}
            currentStoreId={currentStoreId}
            onStoreChanged={this.changeSelectedStore} />
        </div>
      );
    })();

    return (
      <div className={classNames('store-finder', { 'mobile': screenAndStyle.isMobileBrowser() })}>
        <div className='map-container' ref={this.setMapContainerRef}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: apiKey }}
            center={{ lat, lng }}
            zoom={zoom}
            options={{ fullscreenControl: false }}
            onGoogleApiLoaded={this.onGoogleApiLoaded}
            yesIWantToUseGoogleMapApiInternals>

            {yourLocation && <YourLocationPin lat={yourLocation.lat} lng={yourLocation.lng} />}
            {locations.map((location, index) => (
              <StorePin
                key={location.id}
                lat={location.coordinates.latitude}
                lng={location.coordinates.longitude}
                listNumber={index + 1}
                location={location}
                showPopup={location.id === currentStoreId}
                onStoreChanged={this.changeSelectedStore} />
            ))}
          </GoogleMapReact>
        </div>

        <div className='container'>
          {$search}
        </div>
      </div>
    )
  }
}

StoreFinder.propTypes = {
  urls: PropTypes.shape({ nearest_stores: PropTypes.string.isRequired }).isRequired,
  apiKey: PropTypes.string.isRequired,
}

export default StoreFinder;
